<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <div class="d-flex align-center text-orange-accent-4 mt-3">
          <v-icon icon="mdi-alert" color="orange-accent-4" class="mr-2" />
          <span> Notice: Be sure to save after updating values for it to be reflected. </span>
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="8">
        <v-text-field
          v-model="productInstanceSearch"
          label="Search"
          append-icon="mdi-magnify"
          variant="underlined"
          color="primary"
        />
      </v-col>
      <v-col cols="auto" class="d-flex justify-end ml-auto">
        <cs-button
          primary
          label="Add Product Instance"
          aria-label="Add Product Instance"
          class="mt-4"
          left-icon="mdi-plus-circle"
          :disabled="saveInProgress || loadInProgress"
          @click="onAddClick"
        />
      </v-col>
    </v-row>

    <v-data-table
      :items="inputFields"
      :loading="loadInProgress"
      height="400"
      fixed-header
      density="compact"
      disable-sort
      :search="productInstanceSearch"
      :headers="tableHeaders"
    >
      <template #[`item.tenantSubDomain`]="{ item }">
        {{ item.tenantSubDomain || '-' }}
      </template>
      <template #[`item.productInstanceName`]="{ item }">
        {{ item.productInstanceName || '-' }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          icon="mdi-pencil"
          color="primary"
          :disabled="saveInProgress"
          class="mr-2"
          @click="onEditRowClick(item)"
        />
        <v-icon
          icon="mdi-delete"
          color="primary"
          :disabled="saveInProgress"
          @click="onDeleteClick(item)"
        />
      </template>
    </v-data-table>
    <v-row dense class="mt-4">
      <v-col cols="12" class="d-flex justify-end">
        <cs-button
          primary
          label="Save"
          aria-label="Save product instances"
          :loading="saveInProgress"
          :disabled="saveInProgress || loadInProgress"
          @click="onSaveClick()"
        />
      </v-col>
    </v-row>

    <cs-form-dialog
      id="dlg-client-hub-product-instance-management-edit"
      v-model="editDialogDisplayed"
      body-id="dlg-client-hub-product-instance-management-edit-body"
      :heading="editingIndex !== null ? 'Edit Product Instance' : 'Add Product Instance'"
      :primary-action="{
        label: editingIndex !== null ? 'Update' : 'Add',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onFormSubmit()"
      @secondary1-click="onFormCancel()"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.tenantSubDomain"
                label="Tenant Sub Domain"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tenantSubDomain"
                @input="formErrors.tenantSubDomain = []"
                @blur="trimField(formFields, 'tenantSubDomain')"
              />
            </v-col>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.productInstanceName"
                label="Product Instance Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.productInstanceName"
                @input="formErrors.productInstanceName = []"
                @blur="trimField(formFields, 'productInstanceName')"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.productCode"
                label="Product Code"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.productCode"
                @input="formErrors.productCode = []"
                @blur="trimField(formFields, 'productCode')"
              />
            </v-col>

            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.productInstanceIdentifier"
                label="Product Instance Identifier"
                variant="underlined"
                color="primary"
                hint="Optional — auto-generated from ProductCode:ProductInstanceName if left empty"
                persistent-hint
                :error-messages="formErrors.productInstanceIdentifier"
                @input="formErrors.productInstanceIdentifier = []"
                @blur="trimField(formFields, 'productInstanceIdentifier')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="field-container">
              <v-text-field
                v-model="formFields.productInstanceBaseUrl"
                label="Product Instance Base URL"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.productInstanceBaseUrl"
                @input="formErrors.productInstanceBaseUrl = []"
                @blur="trimField(formFields, 'productInstanceBaseUrl')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="field-container">
              <v-text-field
                v-model="formFields.productInstanceCallbackUrl"
                label="Product Instance Callback URL"
                variant="underlined"
                color="primary"
                hint="Multiple URLs separated by |"
                persistent-hint
                :error-messages="formErrors.productInstanceCallbackUrl"
                @input="formErrors.productInstanceCallbackUrl = []"
                @blur="trimField(formFields, 'productInstanceCallbackUrl')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="field-container">
              <v-text-field
                v-model="formFields.productInstanceSignoutUrl"
                label="Product Instance Signout URL"
                variant="underlined"
                color="primary"
                hint="Multiple URLs separated by |"
                persistent-hint
                :error-messages="formErrors.productInstanceSignoutUrl"
                @input="formErrors.productInstanceSignoutUrl = []"
                @blur="trimField(formFields, 'productInstanceSignoutUrl')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="field-container">
              <v-text-field
                v-model="formFields.adminUrl"
                label="Admin URL"
                variant="underlined"
                color="primary"
                hint="Optional"
                persistent-hint
                :error-messages="formErrors.adminUrl"
                @input="formErrors.adminUrl = []"
                @blur="trimField(formFields, 'adminUrl')"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" class="checkbox-container">
              <v-checkbox
                v-model="formFields.isDefault"
                label="Is Default"
                color="primary"
                :error-messages="formErrors.isDefault"
                @update:model-value="formErrors.isDefault = []"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-product-instance-management-save-success"
      v-model="showSaveSuccessDialog"
      heading="Save Success"
      body-id="dlg-client-hub-product-instance-management-save-success-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveSuccessDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <span> The product instance information has been saved successfully. </span>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-product-instance-management-save-error"
      v-model="showSaveErrorDialog"
      heading="Save Error"
      body-id="dlg-client-hub-product-instance-management-save-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>
                  There is some error while saving the product instance information. Please try
                  again.
                </p>
                <p class="text-red-accent-4">{{ errorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-product-instance-management-validation-error"
      v-model="showValidationErrorDialog"
      heading="Validation Error"
      body-id="dlg-client-hub-product-instance-management-validation-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showValidationErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p class="text-red-accent-4">{{ validationErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-product-instance-management-load-error"
      v-model="showLoadErrorDialog"
      heading="Load Error"
      body-id="dlg-client-hub-product-instance-management-load-error-body"
      :primary-action="{
        label: 'Retry',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Close',
        eventName: 'secondary1-click'
      }"
      @primary-click="fetchProductInstanceInfo()"
      @secondary1-click="showLoadErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>
                  There was an error loading the product instance information. Please try again.
                </p>
                <p class="text-red-accent-4">{{ loadErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import * as types from '../store/mutationTypes';

const MAX_CALLBACK_URLS = 100; // mirrors se-stb-api Joi cap at plugins/stb-hub/services/hub-migration/csvSchemas/productInstances.js:28

const DEFAULT_ERRORS = {
  tenantSubDomain: '',
  productInstanceName: '',
  productCode: '',
  productInstanceIdentifier: '',
  productInstanceBaseUrl: '',
  productInstanceCallbackUrl: '',
  productInstanceSignoutUrl: '',
  adminUrl: ''
};

const DEFAULT_INPUT_FIELDS = {
  tenantSubDomain: '',
  productInstanceName: '',
  isDefault: true,
  productCode: '',
  productInstanceIdentifier: '',
  productInstanceBaseUrl: '',
  productInstanceCallbackUrl: '',
  productInstanceSignoutUrl: '',
  adminUrl: '',
  errors: { ...DEFAULT_ERRORS }
};

export default {
  name: 'ClientHubProductInstanceManagement',

  props: {
    clientSid: { type: String, required: false, default: '' }
  },

  emits: ['save-success'],

  data() {
    return {
      productInstanceSearch: '',
      tableHeaders: [
        { title: 'Tenant Sub Domain', key: 'tenantSubDomain' },
        { title: 'Product Instance Name', key: 'productInstanceName' },
        { title: 'Actions', key: 'actions', align: 'center' }
      ],
      editDialogDisplayed: false,
      editingIndex: null,
      inputFields: [],
      formFields: cloneDeep(DEFAULT_INPUT_FIELDS),
      formErrors: cloneDeep(DEFAULT_ERRORS),
      saveInProgress: false,
      showSaveSuccessDialog: false,
      showSaveErrorDialog: false,
      showValidationErrorDialog: false,
      validationErrorMessage: '',
      errorMessage: '',
      loadInProgress: false,
      showLoadErrorDialog: false,
      loadErrorMessage: ''
    };
  },

  async mounted() {
    await this.fetchProductInstanceInfo();
  },

  methods: {
    async fetchProductInstanceInfo() {
      if (!this.clientSid) return;
      try {
        this.loadInProgress = true;
        this.showLoadErrorDialog = false;
        this.loadErrorMessage = '';

        const response = await this.$store.dispatch('hub-migration/getCsvProductInstances', {
          clientSid: this.clientSid
        });

        if (response && response.productInstanceInfo && response.productInstanceInfo.length > 0) {
          this.inputFields = response.productInstanceInfo.map((item) => ({
            tenantSubDomain: item.TenantSubDomain || '',
            productInstanceName: item.ProductInstanceName || '',
            isDefault:
              item.IsDefault === true ||
              (typeof item.IsDefault === 'string' && item.IsDefault.toLowerCase() === 'true'),
            productCode: item.ProductCode || '',
            productInstanceIdentifier: item.ProductInstanceIdentifier || '',
            productInstanceBaseUrl: item.ProductInstanceBaseUrl || '',
            productInstanceCallbackUrl: item.ProductInstanceCallbackUrl || '',
            productInstanceSignoutUrl: item.ProductInstanceSignoutUrl || '',
            adminUrl: item.AdminUrl || '',
            errors: { ...DEFAULT_ERRORS }
          }));
        } else {
          this.inputFields = [];
        }
      } catch (error) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.loadErrorMessage =
          error?.response?.data?.message || error.message || 'Failed to load product instance data';
        this.showLoadErrorDialog = true;
      } finally {
        this.loadInProgress = false;
      }
    },

    trimField(item, field) {
      if (typeof item[field] === 'string') {
        item[field] = item[field].trim();
      }
    },

    normalizePipeSeparatedUrls(item, field) {
      if (typeof item[field] === 'string') {
        item[field] = item[field]
          .split('|')
          .map((url) => url.trim())
          .join('|');
      }
    },

    onDeleteClick(item) {
      const index = this.inputFields.indexOf(item);
      if (index === -1) return;
      this.inputFields.splice(index, 1);
    },

    onAddClick() {
      this.editingIndex = null;
      this.formFields = cloneDeep(DEFAULT_INPUT_FIELDS);
      this.formErrors = cloneDeep(DEFAULT_ERRORS);
      this.editDialogDisplayed = true;
    },

    onEditRowClick(item) {
      const index = this.inputFields.indexOf(item);
      if (index === -1) return;
      this.editingIndex = index;
      this.formFields = cloneDeep(this.inputFields[index]);
      this.formErrors = cloneDeep(DEFAULT_ERRORS);
      this.editDialogDisplayed = true;
    },

    onFormSubmit() {
      const isValid = this.validateProductInstanceInfo(this.formFields);
      if (!isValid) return;

      if (!this.isProductInstanceNameUnique(this.formFields)) return;

      this.normalizePipeSeparatedUrls(this.formFields, 'productInstanceCallbackUrl');
      this.normalizePipeSeparatedUrls(this.formFields, 'productInstanceSignoutUrl');

      if (this.editingIndex !== null) {
        this.inputFields[this.editingIndex] = cloneDeep(this.formFields);
      } else {
        this.inputFields.push(cloneDeep(this.formFields));
      }
      this.editDialogDisplayed = false;
    },

    onFormCancel() {
      this.editDialogDisplayed = false;
    },

    isProductInstanceNameUnique(item) {
      const duplicate = this.inputFields.some((row, index) => {
        if (this.editingIndex !== null && index === this.editingIndex) return false;
        return (
          row.tenantSubDomain === item.tenantSubDomain &&
          row.productInstanceName === item.productInstanceName
        );
      });
      if (duplicate) {
        this.formErrors.productInstanceName =
          'Product instance name must be unique within a tenant';
        return false;
      }
      return true;
    },

    validateTenantSubDomain(v) {
      if (!v) return 'Subdomain is required';
      if (v.length < 2) return 'Minimum 2 characters';
      if (v.length > 31) return 'Maximum 31 characters';
      return true;
    },

    validateProductInstanceName(v) {
      if (!v) return 'Product instance name is required';
      if (v.length > 100) return 'Maximum 100 characters';
      return true;
    },

    validateProductCode(v) {
      if (!v) return 'Product code is required';
      return true;
    },

    validateProductInstanceIdentifier(v) {
      if (!v) return true;
      if (!/^[a-zA-Z0-9 \-_:]*$/.test(v)) {
        return 'Only alphanumeric, space, hyphen, underscore, and colon allowed';
      }
      return true;
    },

    validateUrl(v, label, required) {
      if (!v && required) return `${label} is required`;
      if (!v) return true;
      try {
        const url = new URL(v);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          return `${label} must be a valid http or https URL`;
        }
        return true;
      } catch {
        return `${label} must be a valid URL`;
      }
    },

    validatePipeSeparatedUrls(v, label) {
      if (!v) return `${label} is required`;
      const urls = v.split('|');
      const nonEmpty = urls.map((u) => u.trim()).filter((u) => u);
      if (nonEmpty.length > MAX_CALLBACK_URLS) {
        return `${label} must have less than or equal to ${MAX_CALLBACK_URLS} URIs`;
      }
      for (const url of urls) {
        const trimmed = url.trim();
        if (!trimmed) return `${label} contains an empty URL segment`;
        const result = this.validateUrl(trimmed, label, true);
        if (result !== true) return result;
      }
      return true;
    },

    validateProductInstanceInfo(item) {
      let isValid = true;
      const tenantSubDomainResult = this.validateTenantSubDomain(item.tenantSubDomain);
      const productInstanceNameResult = this.validateProductInstanceName(item.productInstanceName);
      const productCodeResult = this.validateProductCode(item.productCode);
      const productInstanceIdentifierResult = this.validateProductInstanceIdentifier(
        item.productInstanceIdentifier
      );
      const productInstanceBaseUrlResult = this.validateUrl(
        item.productInstanceBaseUrl,
        'Base URL',
        true
      );
      const productInstanceCallbackUrlResult = this.validatePipeSeparatedUrls(
        item.productInstanceCallbackUrl,
        'Callback URL'
      );
      const productInstanceSignoutUrlResult = this.validatePipeSeparatedUrls(
        item.productInstanceSignoutUrl,
        'Signout URL'
      );
      const adminUrlResult = this.validateUrl(item.adminUrl, 'Admin URL', false);

      this.formErrors = {
        tenantSubDomain: tenantSubDomainResult === true ? '' : tenantSubDomainResult,
        productInstanceName: productInstanceNameResult === true ? '' : productInstanceNameResult,
        productCode: productCodeResult === true ? '' : productCodeResult,
        productInstanceIdentifier:
          productInstanceIdentifierResult === true ? '' : productInstanceIdentifierResult,
        productInstanceBaseUrl:
          productInstanceBaseUrlResult === true ? '' : productInstanceBaseUrlResult,
        productInstanceCallbackUrl:
          productInstanceCallbackUrlResult === true ? '' : productInstanceCallbackUrlResult,
        productInstanceSignoutUrl:
          productInstanceSignoutUrlResult === true ? '' : productInstanceSignoutUrlResult,
        adminUrl: adminUrlResult === true ? '' : adminUrlResult
      };

      if (
        tenantSubDomainResult !== true ||
        productInstanceNameResult !== true ||
        productCodeResult !== true ||
        productInstanceIdentifierResult !== true ||
        productInstanceBaseUrlResult !== true ||
        productInstanceCallbackUrlResult !== true ||
        productInstanceSignoutUrlResult !== true ||
        adminUrlResult !== true
      ) {
        isValid = false;
      }
      return isValid;
    },

    async onSaveClick() {
      if (this.inputFields.length === 0) {
        this.showValidationErrorDialog = true;
        this.validationErrorMessage =
          'At least one Product Instance record(s) is required to save.';
        return;
      }

      try {
        if (this.saveInProgress) return;
        this.saveInProgress = true;

        const productInstanceInfo = cloneDeep(this.inputFields).map(({ errors, ...item }) => {
          return {
            TenantSubDomain: item.tenantSubDomain,
            ProductInstanceName: item.productInstanceName,
            IsDefault: item.isDefault,
            ProductCode: item.productCode,
            ProductInstanceIdentifier: item.productInstanceIdentifier,
            ProductInstanceBaseUrl: item.productInstanceBaseUrl,
            ProductInstanceCallbackUrl: item.productInstanceCallbackUrl,
            ProductInstanceSignoutUrl: item.productInstanceSignoutUrl,
            AdminUrl: item.adminUrl
          };
        });

        await this.$store.dispatch('hub-migration/postCsvProductInstances', {
          clientSid: this.clientSid,
          productInstanceInfo
        });
        this.editDialogDisplayed = false;
        this.showSaveSuccessDialog = true;
        this.$emit('save-success');
      } catch (error) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.errorMessage = error?.response?.data?.message || error.message;
        this.showSaveErrorDialog = true;
      } finally {
        this.saveInProgress = false;
      }
    }
  }
};
</script>
<style scoped>
.field-container {
  padding: 8px 10px 8px 10px;
}
.checkbox-container {
  padding: 12px 0px 0px 0px;
}
</style>
